import { ImoovaImage } from "@/app/Media/Components/Image";
import { MediaFieldsFragment } from "@/gql/graphql";
import { cn } from "@/lib/utils";
import { FileTextIcon } from "lucide-react";

type PreviewFile = { mime_type?: string | null; url?: string | null };

export function isPdfUrl(url?: string | null) {
  if (!url) return false;

  return new URL(url, window.location.origin).pathname
    .toLowerCase()
    .endsWith(".pdf");
}

export function isPdf(file?: PreviewFile | null) {
  if (!file) return false;

  return file.mime_type === "application/pdf" || isPdfUrl(file.url);
}

/**
 * Receipts can be PDFs, which an <img> renders as a broken icon.
 */
export function FileThumbnail({
  media,
  src,
  mimeType,
  alt,
  className,
}: {
  media?: MediaFieldsFragment | null;
  src?: string;
  /** Needed when src is an object URL, which carries no file extension. */
  mimeType?: string | null;
  alt: string;
  className?: string;
}) {
  if (media ? isPdf(media) : isPdf({ mime_type: mimeType, url: src })) {
    return (
      <div
        className={cn(
          "flex flex-col items-center justify-center gap-1 bg-zinc-100 dark:bg-zinc-800",
          className,
        )}
      >
        <FileTextIcon className="size-5 text-zinc-400" />
        <span className="text-[10px] font-medium text-zinc-500">PDF</span>
      </div>
    );
  }

  if (media) {
    return <ImoovaImage media={media} alt={alt} className={className} />;
  }

  return <img src={src} alt={alt} className={className} />;
}

export function FilePreview({
  media,
  alt,
  className,
  pdfClassName = "h-[70vh] w-full rounded-lg border",
}: {
  media: MediaFieldsFragment;
  alt: string;
  className?: string;
  /** Image classes rarely suit an iframe, so PDFs get their own. */
  pdfClassName?: string;
}) {
  if (isPdf(media)) {
    return <iframe src={media.url} title={alt} className={pdfClassName} />;
  }

  return <ImoovaImage media={media} alt={alt} className={className} />;
}
